import { useEffect } from 'react';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';

export interface DrawerActions {
  title: string;
  handler: () => void | Promise<void>;
  className?: string;
}

export interface DrawerProps {
  className?: string;
  title: string;
  children: React.ReactNode | React.ReactNode[];
  direction?: 'left' | 'right';
  open: boolean;
  size?: 'sm' | 'md' | 'lg' | 'full';
  onClose: () => void;
  saveIsDisabled?: boolean;
  actions?: DrawerActions[];
  footer?: boolean;
}

const sizes = {
  sm: 'w-[380px]',
  md: 'w-[564px]',
  lg: 'w-[768px]',
  full: 'w-full',
};

const Drawer = (props: DrawerProps): JSX.Element => {
  const direction = props.direction || 'right';
  const size = props.size || 'md';

  // On ESC key press, close panel
  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        props.onClose();
      }
    };
    document.addEventListener('keyup', onKeyUp);
    return () => document.removeEventListener('keyup', onKeyUp);
  }, [props]);

  if (!props.open) {
    return <></>;
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop with Blur Effect */}
      <div
        data-testid="drawer-backdrop"
        className="absolute inset-0 backdrop-blur-sm"
      />

      {/* Drawer Panel */}
      <div
        className={cn(
          'fixed inset-y-0 flex flex-col shadow-lg transition-transform duration-300 ease-in-out bg-level-01',
          sizes[size],
          props.className,
          direction === 'right' ? 'right-0' : 'left-0',
        )}
      >
        <div className="flex items-center justify-between p-md text-white">
          <h2 className="flex-1 text-body-lg font-bold">{props.title}</h2>
          <button className="p-sm text-white" onClick={props.onClose}>
            <Icon type="times" className="text-white" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-md">{props.children}</div>
        <div className="flex justify-end border-t px-sm py-md border-emphasis-subtle">
          {props.actions?.map((action, index) => (
            <button
              className={cn(
                'mr-sm rounded-md border px-md py-sm text-body-sm font-semibold border-emphasis-subtle text-white hover:bg-primary-default',
                action.className,
                props.saveIsDisabled && 'pointer-events-none opacity-subtle',
              )}
              disabled={props.saveIsDisabled}
              key={index}
              onClick={() => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                action.handler();
              }}
            >
              {action.title}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

Drawer.displayName = 'Drawer';

export { Drawer };
